import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Container, Box, Typography, TextField, Button, Paper, Alert, Tabs, Tab, IconButton, CssBaseline, List, ListItem, ListItemText, Divider, CircularProgress, InputAdornment, Menu, MenuItem } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import { motion } from 'framer-motion';
import io from 'socket.io-client';
import Picker from '@emoji-mart/react';
import AnimatedBackground from './components/AnimatedBackground';
import NetworkBackground from './components/NetworkBackground';
import SidebarWeb from './components/SidebarWeb';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GifIcon from '@mui/icons-material/Gif';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Dialog from '@mui/material/Dialog';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const SOCKET_URL = 'https://t-mess.th0rn.ru';
const ACCENT = '#00AEEF';

const MODERN_FONT = [
  'Inter',
  'SF Pro Display',
  'Segoe UI',
  'Roboto',
  'Arial',
  'sans-serif'
].join(',');

function getAvatarUrl(avatar) {
  if (!avatar) return '';
  if (avatar.startsWith('http')) return avatar;
  return API_URL.replace('/api', '') + avatar;
}

function Logo() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <img src="/logo.png" alt="Логотип" style={{ maxWidth: 120, maxHeight: 120 }} />
    </Box>
  );
}

function ThemeToggle({ mode, toggle }) {
  return (
    <Box sx={{ position: 'absolute', top: 24, right: 32, zIndex: 10 }}>
      <motion.div
        whileTap={{ rotate: 360, scale: 1.2 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        <IconButton onClick={toggle} size="large" sx={{
          bgcolor: 'transparent',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.07)' },
          transition: '0.2s',
        }}>
          {mode === 'dark' ? (
            <Brightness7Icon sx={{ color: '#FFD600', filter: 'drop-shadow(0 0 4px #FFA000)' }} />
          ) : (
            <Brightness4Icon sx={{ color: '#7ECFFF', filter: 'drop-shadow(0 0 4px #90CAF9)' }} />
          )}
        </IconButton>
      </motion.div>
    </Box>
  );
}

function Auth({ setToken, setUser }) {
  const [tab, setTab] = useState(0);
  const [regData, setRegData] = useState({ username: '', password: '', display_name: '' });
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pending, setPending] = useState([]);
  const [pwChange, setPwChange] = useState({ old: '', new1: '', new2: '' });
  const [pwMsg, setPwMsg] = useState('');

  const handleTab = (_, v) => { setTab(v); setError(''); setSuccess(''); };

  const handleRegChange = e => setRegData({ ...regData, [e.target.name]: e.target.value });
  const handleLoginChange = e => setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const register = async e => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regData)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Ошибка регистрации');
      setSuccess('Заявка отправлена! Ожидайте одобрения администратора.');
    } catch { setError('Ошибка сети'); }
  };

  const login = async e => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Ошибка входа');
      setToken(data.token); localStorage.setItem('token', data.token); setUser(data.user);
      setSuccess('Вход выполнен!');
    } catch { setError('Ошибка сети'); }
  };

  const logout = () => { setToken(''); setUser(null); localStorage.removeItem('token'); };

  useEffect(() => {
    if (setUser && setUser.is_admin) {
      fetch(`${API_URL}/pending-users`, { headers: { Authorization: `Bearer ${setToken}` } })
        .then(r => r.json()).then(setPending).catch(() => setPending([]));
    }
  }, [setToken, setUser]);

  const approve = async (id) => {
    await fetch(`${API_URL}/approve-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${setToken}` },
      body: JSON.stringify({ user_id: id })
    });
    setPending(pending.filter(u => u.id !== id));
  };

  const handlePwChange = e => setPwChange({ ...pwChange, [e.target.name]: e.target.value });
  const changePassword = async e => {
    e.preventDefault(); setPwMsg('');
    if (pwChange.new1 !== pwChange.new2) return setPwMsg('Пароли не совпадают');
    const res = await fetch(`${API_URL}/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${setToken}` },
      body: JSON.stringify({ old_password: pwChange.old, new_password: pwChange.new1 })
    });
    const data = await res.json();
    if (!res.ok) setPwMsg(data.error || 'Ошибка');
    else setPwMsg('Пароль изменён!');
    setPwChange({ old: '', new1: '', new2: '' });
  };

  // Получаем текущую тему из body (mui v5)
  const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches || document.body.classList.contains('Mui-dark');
  const cardSx = {
    p: { xs: 2, sm: 3 },
    pt: 4,
    maxWidth: 340,
    mx: 'auto',
    borderRadius: 2, // 8px
    boxShadow: '0 4px 24px 0 rgba(31, 38, 135, 0.10)',
    background: (theme) => theme.palette.mode === 'dark' ? '#23272f' : '#fff',
    color: (theme) => theme.palette.text.primary,
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
  };
  const titleSx = {
    fontWeight: 700,
    fontSize: 24,
    letterSpacing: 1,
    mb: 2,
    textAlign: 'center',
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
  };
  const tabSx = {
    fontWeight: 600,
    fontSize: 15,
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
    color: ACCENT,
    textTransform: 'uppercase',
    letterSpacing: 1,
  };
  const fieldSx = {
    borderRadius: 2,
    background: (theme) => theme.palette.mode === 'dark' ? '#2e3440' : '#f5f7fa',
    color: (theme) => theme.palette.text.primary,
    input: { color: (theme) => theme.palette.text.primary },
    fontSize: 16,
  };
  const buttonSx = {
    mt: 2,
    borderRadius: 2,
    fontWeight: 600,
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
    fontSize: 16,
    background: ACCENT,
    color: '#fff',
    boxShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.10)',
    transition: '0.2s',
    '&:hover': {
      background: '#008fc7',
      boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.18)',
      transform: 'scale(1.03)'
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 10, fontFamily: 'Inter, Roboto, Arial, sans-serif' }}>
      <Paper sx={cardSx}>
        <Logo />
        <Tabs value={tab} onChange={handleTab} centered TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}>
          <Tab label="Вход" sx={tabSx} />
          <Tab label="Регистрация" sx={tabSx} />
        </Tabs>
        {tab === 0 && (
          <Box component="form" onSubmit={login} sx={{ mt: 2 }}>
            <TextField label="Логин" name="username" fullWidth margin="normal" value={loginData.username} onChange={handleLoginChange} required InputProps={{ sx: fieldSx }} />
            <TextField label="Пароль" name="password" type="password" fullWidth margin="normal" value={loginData.password} onChange={handleLoginChange} required InputProps={{ sx: fieldSx }} />
            <Button type="submit" variant="contained" fullWidth sx={buttonSx}>Войти</Button>
          </Box>
        )}
        {tab === 1 && (
          <Box component="form" onSubmit={register} sx={{ mt: 2 }}>
            <TextField label="Логин" name="username" fullWidth margin="normal" value={regData.username} onChange={handleRegChange} required InputProps={{ sx: fieldSx }} />
            <TextField label="Пароль" name="password" type="password" fullWidth margin="normal" value={regData.password} onChange={handleRegChange} required InputProps={{ sx: fieldSx }} />
            <TextField label="Имя" name="display_name" fullWidth margin="normal" value={regData.display_name} onChange={handleRegChange} InputProps={{ sx: fieldSx }} />
            <Button type="submit" variant="contained" fullWidth sx={buttonSx}>Зарегистрироваться</Button>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      </Paper>
    </Container>
  );
}

function Footer() {
  return (
    <Box sx={{
      position: 'fixed',
      left: 0,
      bottom: 0,
      width: '100%',
      py: 1,
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,32,38,0.75)' : 'rgba(255,255,255,0.75)',
      color: (theme) => theme.palette.text.secondary,
      textAlign: 'center',
      fontSize: 12,
      fontFamily: 'Inter, Roboto, Arial, sans-serif',
      letterSpacing: 0.2,
      zIndex: 1,
      boxShadow: '0 -2px 12px 0 rgba(31,38,135,0.07)'
    }}>
      T-Mess. Приватный мессенджер для безопасного общения. Все права защищены.
    </Box>
  );
}

function UserProfile({ user, onEdit, mode, toggleTheme, userStatuses }) {
  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative', width: 56, height: 56, mr: 2 }}>
        <img src={getAvatarUrl(user.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
        {userStatuses && userStatuses[user.id] && userStatuses[user.id].online && (
          <span style={{ position: 'absolute', bottom: 4, right: 4, width: 12, height: 12, borderRadius: '50%', background: '#4caf50', border: '2px solid #fff' }} />
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>{user.display_name || user.username}</Typography>
        <Typography sx={{ fontSize: 13, color: userStatuses && userStatuses[user.id] && userStatuses[user.id].online ? '#4caf50' : 'text.secondary' }}>Онлайн</Typography>
      </Box>
      <IconButton size="small" onClick={onEdit} title="Редактировать профиль">
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path fill={ACCENT} d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83Z"/></svg>
      </IconButton>
    </Box>
  );
}

function Messenger({ token, user, setUser, logout, mode, toggleTheme }) {
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [menuMsg, setMenuMsg] = useState(null);
  const [msgError, setMsgError] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [profileName, setProfileName] = useState(user.display_name || '');
  const [profileNameChanged, setProfileNameChanged] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [showPwModal, setShowPwModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [pwFields, setPwFields] = useState({ old: '', new1: '', new2: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editUserFields, setEditUserFields] = useState({ display_name: '', username: '', is_admin: false, is_approved: false });
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [addUserQuery, setAddUserQuery] = useState('');
  const [addUserResults, setAddUserResults] = useState([]);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState(null);
  const [memberMenuUser, setMemberMenuUser] = useState(null);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [groupInfo, setGroupInfo] = useState(null);
  const isMobile = window.matchMedia('(max-width: 600px)').matches;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifResults, setGifResults] = useState([]);
  const [gifQuery, setGifQuery] = useState('');
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editGroupTitle, setEditGroupTitle] = useState('');
  const [editGroupAvatarFile, setEditGroupAvatarFile] = useState(null);
  const [editGroupAvatarPreview, setEditGroupAvatarPreview] = useState(null);
  const [contactStatus, setContactStatus] = useState({ online: false, last_active: null });
  // В Messenger, добавить состояние для статусов всех пользователей:
  const [userStatuses, setUserStatuses] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);
  // В Messenger:
  const [forwardMsgId, setForwardMsgId] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [pinnedMsgId, setPinnedMsgId] = useState(null);
  const [reactions, setReactions] = useState({}); // { msgId: [{emoji, userId}] }
  // 1. СТЕЙТЫ для меню загрузки
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const [fileInfos, setFileInfos] = useState({});
  const [preview, setPreview] = useState(null); // { url, type, name }

  // 2. Функция отправки файла
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.file_id) {
        // Отправляем сообщение с file_id
        await fetch(`${API_URL}/chats/${selected}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ file_id: data.file_id })
        });
      }
    } catch (err) {}
    setUploading(false);
  };

  // Получить список чатов
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setChats);
  }, [token]);

  // Получить сообщения выбранного чата
  useEffect(() => {
    if (!selected || !token) return;
    setLoading(true);
    fetch(`${API_URL}/chats/${selected}/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => { setMessages(data); setLoading(false); });
    // socket.io join
    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    }
    socketRef.current.emit('join_chat', selected);
    socketRef.current.on('new_message', m => {
      console.log('SOCKET NEW_MESSAGE', m);
      if (m.chat_id === selected) {
        setMessages(msgs => [...msgs, m]);
        if (m.file && m.file.id) {
          setFileInfos(prev => ({ ...prev, [m.file.id]: m.file }));
        } else if (m.file_id && !fileInfos[m.file_id]) {
          fetch(`${API_URL}/files/${m.file_id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => setFileInfos(prev => ({ ...prev, [m.file_id]: data })));
        }
      }
    });
    return () => {
      socketRef.current.emit('leave_chat', selected);
      socketRef.current.off('new_message');
    };
  }, [selected, token]);

  // Скролл вниз при новых сообщениях и при открытии чата
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, selected]);

  // Отправить сообщение
  const send = async e => {
    e.preventDefault();
    if (!msg.trim()) return;
    const res = await fetch(`${API_URL}/chats/${selected}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: msg })
    });
    setMsg('');
    if (!res.ok) return;
    // сообщение придёт через socket.io
  };

  // Поиск пользователей
  useEffect(() => {
    if (!showUserSearch || !userQuery.trim()) {
      setUserResults([]);
      return;
    }
    setUserSearchLoading(true);
    const timeout = setTimeout(() => {
      fetch(`${API_URL}/users/search?query=${encodeURIComponent(userQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => { setUserResults(data); setUserSearchLoading(false); });
    }, 400);
    return () => clearTimeout(timeout);
  }, [userQuery, showUserSearch, token]);

  // Создать личный чат с пользователем
  const createPrivateChat = async (otherUser) => {
    const res = await fetch(`${API_URL}/chats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ members: [otherUser.id], is_group: false })
    });
    const data = await res.json();
    setShowUserSearch(false);
    setUserQuery('');
    setUserResults([]);
    if (data.chat_id) {
      // Обновить список чатов и выделить новый
      fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(chats => {
          setChats(chats);
          setSelected(data.chat_id);
        });
    }
  };

  // Редактировать сообщение
  const startEdit = (msg) => {
    setEditMsg(msg);
    setEditValue(msg.content);
    setMenuMsg(null);
  };
  const saveEdit = async () => {
    if (!editMsg || !editValue.trim()) return;
    try {
      const res = await fetch(`${API_URL}/chats/${selected}/messages/${editMsg.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: editValue })
      });
      if (!res.ok) {
        const data = await res.json();
        setMsgError(data.error || 'Ошибка редактирования');
        return;
      }
      setEditMsg(null);
      setEditValue('');
      setMsgError('');
      fetch(`${API_URL}/chats/${selected}/messages`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setMessages);
    } catch (e) {
      setMsgError('Ошибка сети');
    }
  };
  // Удалить сообщение
  const confirmDelete = async () => {
    if (!deleteMsg) return;
    try {
      const res = await fetch(`${API_URL}/chats/${selected}/messages/${deleteMsg}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        setMsgError(data.error || 'Ошибка удаления');
        return;
      }
      setDeleteMsg(null);
      setMsgError('');
      fetch(`${API_URL}/chats/${selected}/messages`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setMessages);
    } catch (e) {
      setMsgError('Ошибка сети');
    }
  };

  // Вставка эмодзи в текст
  const addEmoji = (e) => {
    setMsg(msg + e.native);
    setShowEmoji(false);
  };

  useEffect(() => {
    if (showProfile) {
      setProfileName(user.display_name || '');
      setProfileNameChanged(false);
      setProfileMsg('');
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  }, [showProfile, user]);

  // Получение участников группы при открытии инфо
  useEffect(() => {
    if (showGroupInfo && groupInfo) {
      setGroupMembersLoading(true);
      fetch(`${API_URL}/chats/${groupInfo.id}/members`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setGroupMembers).finally(() => setGroupMembersLoading(false));
    }
  }, [showGroupInfo, groupInfo, token]);

  // Поиск пользователей для добавления
  useEffect(() => {
    if (!addUserQuery.trim()) { setAddUserResults([]); return; }
    fetch(`${API_URL}/users/search?query=${encodeURIComponent(addUserQuery)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setAddUserResults);
  }, [addUserQuery, token]);

  useEffect(() => {
    if (isMobile && selected) setSidebarOpen(false);
    if (isMobile && !selected) setSidebarOpen(true);
  }, [isMobile, selected]);

  const handleDeleteChat = async (chatId) => {
    if (!chatId) return;
    await fetch(`${API_URL}/chats/${chatId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    // Обновить список чатов
    fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setChats);
    handleMenuClose();
  };

  // Поиск пользователей для автодополнения в сайдбаре
  useEffect(() => {
    if (!userQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const timeout = setTimeout(() => {
      fetch(`${API_URL}/users/search?query=${encodeURIComponent(userQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => { setSearchResults(data); setSearchLoading(false); });
    }, 300);
    return () => clearTimeout(timeout);
  }, [userQuery, token]);

  // Поиск гифок (через Giphy API, пример)
  useEffect(() => {
    if (!showGifPicker || !gifQuery.trim()) { setGifResults([]); return; }
    fetch(`https://api.giphy.com/v1/gifs/search?api_key=dc6zaTOxFJmzC&q=${encodeURIComponent(gifQuery)}&limit=20`)
      .then(r => r.json()).then(data => setGifResults(data.data || []));
  }, [gifQuery, showGifPicker]);

  const chat = chats.find(c => c.id === selected && !c.is_group);
  const contact = chat && chat.members ? chat.members.find(u => u.id !== user.id) : null;

  useEffect(() => {
    let interval;
    async function fetchStatus() {
      if (chat && contact) {
        const res = await fetch(`${API_URL}/users/${contact.id}/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setContactStatus(data);
        }
      }
    }
    if (chat && contact) {
      fetchStatus();
      interval = setInterval(fetchStatus, 10000); // каждые 10 секунд
    }
    return () => clearInterval(interval);
  }, [chat, contact, token]);

  // Периодически обновлять статусы всех пользователей из списка чатов:
  useEffect(() => {
    async function fetchAllStatuses() {
      const ids = chats.filter(c => !c.is_group).map(c => {
        const contact = c.members ? c.members.find(u => u.id !== user.id) : null;
        return contact ? contact.id : null;
      }).filter(Boolean);
      const statuses = { ...userStatuses };
      await Promise.all(ids.map(async id => {
        const res = await fetch(`${API_URL}/users/${id}/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          statuses[id] = data;
        }
      }));
      statuses[user.id] = { online: true };
      setUserStatuses(statuses);
    }
    if (chats.length) {
      fetchAllStatuses();
      const interval = setInterval(fetchAllStatuses, 10000);
      return () => clearInterval(interval);
    }
  }, [chats, token]);

  // В Messenger, при выборе чата отправлять PATCH /api/chats/:chatId/read:
  useEffect(() => {
    if (!selected || !token) return;
    fetch(`${API_URL}/chats/${selected}/read`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` }
    }).then(() => {
      // После пометки как прочитанных обновить список чатов
      fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setChats);
    });
  }, [selected, token]);

  // Внутри Messenger, для рендера сообщений:
  function formatDate(date) {
    const d = new Date(date);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Сегодня';
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
    return d.toLocaleDateString();
  }

  const handleMenuOpen = (event, chatId) => {
    setMenuAnchor(event.currentTarget);
    setMenuChatId(chatId);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuChatId(null);
  };
  const handleArchiveChat = () => {
    // TODO: реализовать архивирование
    handleMenuClose();
  };
  const handleMenuDeleteChat = () => handleDeleteChat(menuChatId);

  // Загружать инфу о файлах для сообщений с file_id
  useEffect(() => {
    const fileIds = messages.filter(m => m.file_id && !fileInfos[m.file_id]).map(m => m.file_id);
    if (!fileIds.length) return;
    fileIds.forEach(id => {
      fetch(`${API_URL}/files/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(data => setFileInfos(prev => ({ ...prev, [id]: data })));
    });
  }, [messages, token]);

  // Перед messages.map:
  function handleReaction(emoji, reactedByMe, messageId) {
    let called = false;
    return async (e) => {
      if (called) return;
      called = true;
      try {
        e.preventDefault && e.preventDefault();
        if (reactedByMe) {
          await fetch(`${API_URL}/messages/${messageId}/reactions`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ emoji })
          });
        } else {
          await fetch(`${API_URL}/messages/${messageId}/reactions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ emoji })
          });
        }
        const res = await fetch(`${API_URL}/chats/${selected}/messages`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setMessages(data);
        }
      } catch (err) {
        // Можно добавить вывод ошибки в UI
        console.error('Ошибка реакции:', err);
      }
    };
  }

  return (
    <Box sx={{ display: 'flex', height: { xs: '100dvh', sm: '100vh' }, bgcolor: 'background.paper', overflow: 'hidden', fontFamily: `'SF Pro Display', 'San Francisco', 'Segoe UI', 'Roboto', 'Inter', Arial, sans-serif`, position: 'relative' }}>
      {/* Сайдбар */}
      {(!isMobile || sidebarOpen) && (
        <Box sx={{ width: { xs: '100vw', sm: 300 }, display: { xs: sidebarOpen ? 'flex' : 'none', sm: 'flex' }, flexDirection: 'column', height: { xs: '100dvh', sm: '100vh' }, position: 'relative', zIndex: 20, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider' }}>
          <SidebarWeb />
          <UserProfile user={user} onEdit={() => setShowProfile(true)} mode={mode} toggleTheme={toggleTheme} userStatuses={userStatuses} />
          {/* Поиск пользователей */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              placeholder="Поиск пользователей..."
              value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              size="small"
              fullWidth
              sx={{ bgcolor: 'background.default', borderRadius: 2 }}
              InputProps={{
                endAdornment: userQuery && (
                  <IconButton size="small" onClick={() => setUserQuery('')}><svg width="16" height="16" viewBox="0 0 24 24"><path fill={ACCENT} d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.89 4.89a1 1 0 1 0 1.4-1.41L10.59 12l4.89-4.89a1 1 0 0 0 0-1.4z"/></svg></IconButton>
                )
              }}
            />
            {userQuery && searchResults.length > 0 && (
              <Paper sx={{ position: 'absolute', left: 16, right: 16, top: 70, zIndex: 20, maxHeight: 320, overflowY: 'auto' }}>
                <List>
                  {searchResults.map(u => (
                    <ListItem button key={u.id} onClick={async () => {
                      // Создать/открыть личный чат
                      const res = await fetch(`${API_URL}/chats`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ members: [u.id], is_group: false })
                      });
                      const data = await res.json();
                      fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.json())
                        .then(chats => {
                          setChats(chats);
                          setSelected(data.chat_id);
                        });
                      setUserQuery('');
                      setSearchResults([]);
                    }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: ACCENT, mr: 2 }}>
                        {u.avatar
                          ? <img src={u.avatar.startsWith('http') ? u.avatar : API_URL.replace('/api','') + u.avatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                          : (u.display_name ? u.display_name[0].toUpperCase() : u.username[0].toUpperCase())}
                      </Box>
                      <ListItemText primary={u.display_name || u.username} secondary={u.username} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            <IconButton color="primary" size="small" onClick={() => setShowGroupModal(true)} title="Создать группу" sx={{ color: ACCENT }}><AddIcon /></IconButton>
          </Box>
          {/* Список чатов */}
          <List sx={{ flex: 1, overflowY: 'auto' }}>
            {chats.map(chat => {
              const isGroup = chat.is_group;
              const contact = !isGroup && chat.members ? chat.members.find(u => u.id !== user.id) : null;
              return (
                <ListItem button key={chat.id} selected={selected === chat.id} onClick={() => setSelected(chat.id)} sx={{ position: 'relative', pl: 2, pr: 6, py: 1.2, borderRadius: 2, mb: 0.5, bgcolor: selected === chat.id ? 'background.default' : 'transparent', transition: '0.2s', '&:hover': { bgcolor: 'background.default' } }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: ACCENT, mr: 2, position: 'relative' }}>
                    {isGroup
                      ? (chat.avatar
                          ? <img src={getAvatarUrl(chat.avatar)} alt="group" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                          : chat.title ? chat.title[0].toUpperCase() : 'G')
                      : (contact && contact.avatar
                          ? <img src={getAvatarUrl(contact.avatar)} alt="contact" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                          : contact ? (contact.display_name ? contact.display_name[0].toUpperCase() : contact.username[0].toUpperCase()) : '?')}
                    {/* Зелёная точка онлайн для личных чатов */}
                    {!isGroup && contact && userStatuses[contact.id] && userStatuses[contact.id].online && (
                      <span style={{ position: 'absolute', bottom: 4, right: 4, width: 10, height: 10, borderRadius: '50%', background: '#4caf50', border: '2px solid #fff' }} />
                    )}
                    {/* Индикатор непрочитанных сообщений */}
                    {chat.unread > 0 && (
                      <span style={{ position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, borderRadius: 8, background: '#2196f3', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: '2px solid #fff' }}>
                        {chat.unread > 9 ? '9+' : chat.unread}
                      </span>
                    )}
                  </Box>
                  {/* Имя чата */}
                  <ListItemText
                    primary={isGroup ? chat.title : (contact ? (contact.display_name || contact.username) : 'Личный чат')}
                    secondary={isGroup ? 'Группа' : 'Личное'}
                  />
                  {/* Меню с тремя точками */}
                  <IconButton size="small" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', opacity: 0.7 }} onClick={e => { e.stopPropagation(); handleMenuOpen(e, chat.id); }}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem onClick={handleArchiveChat}>В архив</MenuItem>
            <MenuItem onClick={handleMenuDeleteChat} sx={{ color: 'error.main' }}>Удалить</MenuItem>
          </Menu>
          <Divider />
          <Box sx={{ position: 'absolute', left: 0, bottom: 0, width: '100%', bgcolor: 'background.paper', p: 2, zIndex: 10, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1, minHeight: 64 }}>
            <img src="/logo.png" alt="logo" style={{ width: 44, height: 44, marginRight: 8 }} />
            <span style={{ fontSize: 13, color: ACCENT, fontWeight: 500, textAlign: 'center' }}>версия 1.0</span>
          </Box>
        </Box>
      )}
      {/* Окно сообщений */}
      {(!isMobile || !sidebarOpen) && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: { xs: '100dvh', sm: '100vh' }, position: 'relative', minWidth: 0, background: 'transparent' }}>
          <NetworkBackground />
          {/* Шапка чата на мобильном */}
          {isMobile && (
            <Box sx={{ height: 56, display: 'flex', alignItems: 'center', px: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', zIndex: 30 }}>
              <IconButton onClick={() => { setSidebarOpen(true); setSelected(null); }}><svg width="28" height="28" viewBox="0 0 24 24"><path d="M15.5 19a1 1 0 0 1-.7-1.7L18.1 14H6a1 1 0 1 1 0-2h12.1l-3.3-3.3a1 1 0 1 1 1.4-1.4l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-.7.3z" fill={ACCENT}/></svg></IconButton>
              <Typography sx={{ fontWeight: 700, ml: 1 }}>{/* Имя чата/группы */}</Typography>
              {isMobile && selected && chats.find(c => c.id === selected && c.is_group) && (
                <IconButton onClick={async () => {
                  // Получить инфо о группе (можно через chats.find или отдельный fetch)
                  const group = chats.find(c => c.id === selected);
                  setGroupInfo(group);
                  setShowGroupInfo(true);
                }} sx={{ ml: 'auto' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke={ACCENT} strokeWidth="2"/><text x="12" y="16" textAnchor="middle" fontSize="12" fill={ACCENT}>i</text></svg>
                </IconButton>
              )}
            </Box>
          )}
          {/* Шапка чата на десктопе */}
          {!isMobile && selected && chats.find(c => c.id === selected && c.is_group) && (
            <Box sx={{ height: 64, display: 'flex', alignItems: 'center', px: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', cursor: 'pointer', zIndex: 30 }}
              onClick={() => {
                const group = chats.find(c => c.id === selected);
                setGroupInfo(group);
                setShowGroupInfo(true);
              }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: ACCENT, mr: 2 }}>
                {chats.find(c => c.id === selected).avatar
                  ? <img src={getAvatarUrl(chats.find(c => c.id === selected).avatar)} alt="group" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  : chats.find(c => c.id === selected).title ? chats.find(c => c.id === selected).title[0].toUpperCase() : 'G'}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{chats.find(c => c.id === selected).title}</Typography>
            </Box>
          )}
          {/* Шапка чата для личных чатов */}
          {!isMobile && selected && chat && contact && (
            <Box sx={{ height: 64, display: 'flex', alignItems: 'center', px: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', zIndex: 30 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: ACCENT, mr: 2, position: 'relative' }}>
                {contact.avatar
                  ? <img src={getAvatarUrl(contact.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  : (contact.display_name ? contact.display_name[0].toUpperCase() : contact.username[0].toUpperCase())}
                {userStatuses[contact.id] && userStatuses[contact.id].online && (
                  <span style={{ position: 'absolute', bottom: 4, right: 4, width: 10, height: 10, borderRadius: '50%', background: '#4caf50', border: '2px solid #fff' }} />
                )}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{contact.display_name || contact.username}</Typography>
                <Typography sx={{ fontSize: 14, color: contactStatus.online ? '#4caf50' : 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {contactStatus.online
                    ? 'в сети'
                    : contactStatus.last_active ? `был(а) в сети ${formatLastActive(contactStatus.last_active)}` : 'офлайн'}
                </Typography>
              </Box>
            </Box>
          )}
          {/* Контейнер сообщений — flex: 1, скролл, без лишних pb/height */}
          <Box
            sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 7,
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(120,120,120,0.4) transparent',
              '&::-webkit-scrollbar': { width: 8, background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(120,120,120,0.4)', borderRadius: 4, transition: 'background 0.2s' },
              '&:hover::-webkit-scrollbar-thumb': { background: 'rgba(120,120,120,0.7)' }
            }}
            onTouchStart={e => {
              if (window.innerWidth <= 800 && e.touches[0].clientX < 30) {
                Messenger._swipeBackStartX = e.touches[0].clientX;
                Messenger._swipeBackStartY = e.touches[0].clientY;
                Messenger._swipeBack = true;
              }
            }}
            onTouchMove={e => {
              if (window.innerWidth <= 800 && Messenger._swipeBack) {
                const dx = e.touches[0].clientX - Messenger._swipeBackStartX;
                const dy = Math.abs(e.touches[0].clientY - Messenger._swipeBackStartY);
                if (dx > 60 && dy < 40) {
                  setSelected(null);
                  Messenger._swipeBack = false;
                }
              }
            }}
            onTouchEnd={e => { Messenger._swipeBack = false; }}
          >
            {loading ? <CircularProgress /> : (() => { 
              let lastDate = null;
              const chat = chats.find(c => c.id === selected);
              const contact = chat && chat.members ? chat.members.find(u => u.id !== user.id) : null;
              return messages.map((m, i) => {
                const msgDate = formatDate(m.created_at);
                const showDate = msgDate !== lastDate;
                lastDate = msgDate;
                const isOwn = m.user_id === user.id;
                const sender = chat && chat.members ? chat.members.find(u => u.id === m.user_id) : null;
                return (
                  <React.Fragment key={m.id}>
                    {showDate && (
                      <Box sx={{ textAlign: 'center', my: 2 }}>
                        <span style={{ background: '#222', color: '#fff', borderRadius: 8, padding: '2px 12px', fontSize: 13, opacity: 0.7 }}>{msgDate}</span>
                      </Box>
                    )}
                    <Box
                      sx={{ mb: 1.2, display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', position: 'relative' }}
                      onContextMenu={e => {
                        if (window.innerWidth > 800) {
                          e.preventDefault();
                          setMenuMsg(m.id);
                          setMenuAnchor(e.currentTarget);
                        }
                      }}
                      onTouchStart={e => {
                        if (window.innerWidth <= 800) {
                          m._touchTimeout = setTimeout(() => {
                            setMenuMsg(m.id);
                            setMenuAnchor(e.currentTarget);
                          }, 500);
                        }
                      }}
                      onTouchEnd={e => {
                        if (window.innerWidth <= 800 && m._touchTimeout) {
                          clearTimeout(m._touchTimeout);
                          m._touchTimeout = null;
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        {/* Аватар только для чужих сообщений */}
                        {!isOwn && sender && (
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: ACCENT, mr: 1, position: 'relative' }}>
                            {sender.avatar
                              ? <img src={getAvatarUrl(sender.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                              : (sender.display_name ? sender.display_name[0].toUpperCase() : sender.username[0].toUpperCase())}
                            {userStatuses[sender.id] && userStatuses[sender.id].online && (
                              <span style={{ position: 'absolute', bottom: 2, right: 2, width: 8, height: 8, borderRadius: '50%', background: '#4caf50', border: '2px solid #fff' }} />
                            )}
                          </Box>
                        )}
                        <Paper sx={{
                          p: 1.5,
                          bgcolor: isOwn ? ACCENT : (mode === 'light' ? '#f1f0f0' : 'background.paper'),
                          color: isOwn ? '#fff' : 'text.primary',
                          borderRadius: isOwn ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                          maxWidth: { xs: '95vw', sm: 440 },
                          minWidth: { xs: '40vw', sm: 0 },
                          position: 'relative',
                          wordBreak: 'break-word',
                          px: 1.5,
                          pr: 3,
                          py: 0.7,
                          boxShadow: 0,
                          fontFamily: `'SF Pro Display', 'San Francisco', 'Segoe UI', 'Roboto', 'Inter', Arial, sans-serif`,
                        }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', mb: 0.2, color: ACCENT }}>
                            {!isOwn && sender && (sender.display_name || sender.username)}
                          </Typography>
                          <Typography sx={{ fontSize: 15 }}>{m.content}</Typography>
                          {m.file_id && fileInfos[m.file_id] && (
                            (() => {
                              const f = fileInfos[m.file_id];
                              if (f.type && f.type.startsWith('image/')) {
                                return <img src={f?.url || '/default.png'} alt={f.original_name} style={{ maxWidth: 220, maxHeight: 220, borderRadius: 12, marginTop: 6, cursor: 'pointer' }} onClick={() => setPreview({ url: f.url, type: f.type, name: f.original_name })} />;
                              }
                              if (f.type && f.type.startsWith('video/')) {
                                return <video src={f?.url || '#'} controls style={{ maxWidth: 220, maxHeight: 220, borderRadius: 12, marginTop: 6, cursor: 'pointer' }} onClick={() => setPreview({ url: f.url, type: f.type, name: f.original_name })} />;
                              }
                              return <a href={f?.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, marginTop: 6, display: 'inline-block' }}>{f.original_name || 'Файл'}</a>;
                            })()
                          )}
                        </Paper>
                        {/* Время сообщения */}
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 1, minWidth: 48, textAlign: 'right', alignSelf: 'flex-end' }}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                      </Box>
                    </Box>
                    {/* Реакции под сообщением */}
                    {m.reactions && m.reactions.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                        {Array.from(new Set(m.reactions.map(r => r.emoji))).map(emoji => {
                          const count = m.reactions.filter(r => r.emoji === emoji).length;
                          const reactedByMe = m.reactions.some(r => r.emoji === emoji && r.user_id === user.id);
                          return (
                            <span
                              key={emoji}
                              style={{
                                fontSize: window.innerWidth <= 800 ? 28 : 18,
                                background: reactedByMe ? '#e0f7fa' : '#f5f5f5',
                                borderRadius: 12,
                                padding: window.innerWidth <= 800 ? '10px 20px' : '0 8px',
                                marginRight: window.innerWidth <= 800 ? 8 : 0,
                                cursor: 'pointer',
                                border: reactedByMe ? '2px solid #00aeef' : '1.5px solid #ddd',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: reactedByMe ? 700 : 500,
                                minHeight: window.innerWidth <= 800 ? 48 : 28,
                                minWidth: window.innerWidth <= 800 ? 48 : 28,
                                justifyContent: 'center',
                                boxShadow: window.innerWidth <= 800 ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.15s',
                              }}
                              {...(window.innerWidth <= 800
                                ? { onTouchEnd: handleReaction(emoji, reactedByMe, m.id) }
                                : { onClick: handleReaction(emoji, reactedByMe, m.id) })}
                            >
                              {emoji} {count > 1 && <span style={{ fontSize: 13, marginLeft: 2 }}>{count}</span>}
                            </span>
                          );
                        })}
                      </Box>
                    )}
                    {/* Индикатор закреплённого сообщения */}
                    {pinnedMsgId === m.id && (
                      <Box sx={{ position: 'absolute', top: -18, left: isOwn ? 'auto' : -24, right: isOwn ? -24 : 'auto', color: '#ff9800' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M17.707 10.293l-4-4A1 1 0 0 0 12 6H7a1 1 0 0 0-1 1v5a1 1 0 0 0 .293.707l4 4a1 1 0 0 0 1.414 0l5-5a1 1 0 0 0 0-1.414z"/></svg>
                      </Box>
                    )}
                    <Menu
                      anchorEl={menuAnchor}
                      open={menuMsg === m.id && Boolean(menuAnchor)}
                      onClose={() => { setMenuMsg(null); setMenuAnchor(null); }}
                      anchorOrigin={{ vertical: 'top', horizontal: isOwn ? 'right' : 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: isOwn ? 'right' : 'left' }}
                    >
                      <MenuItem disableRipple disableTouchRipple style={{ pointerEvents: 'none', opacity: 0.7, fontSize: 13 }}>Добавить реакцию</MenuItem>
                      <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 1 }}>
                        {["👍", "❤️", "😂", "😮", "😢", "👎"].map(emoji => (
                          <IconButton key={emoji} size="small" onClick={async () => {
                            const reactedByMe = m.reactions && m.reactions.some(r => r.emoji === emoji && r.user_id === user.id);
                            if (reactedByMe) {
                              await fetch(`${API_URL}/messages/${m.id}/reactions`, {
                                method: 'DELETE',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ emoji })
                              });
                            } else {
                              await fetch(`${API_URL}/messages/${m.id}/reactions`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ emoji })
                              });
                            }
                            // Обновить сообщения
                            fetch(`${API_URL}/chats/${selected}/messages`, { headers: { Authorization: `Bearer ${token}` } })
                              .then(r => r.json()).then(setMessages);
                            setMenuMsg(null); setMenuAnchor(null);
                          }}>{emoji}</IconButton>
                        ))}
                      </Box>
                      {isOwn && <MenuItem onClick={() => { startEdit(m); setMenuMsg(null); setMenuAnchor(null); }}>Редактировать</MenuItem>}
                      <MenuItem onClick={() => { setForwardMsgId(m.id); setShowForwardModal(true); setMenuMsg(null); setMenuAnchor(null); }}>Переслать</MenuItem>
                      {isOwn && <MenuItem onClick={() => { setDeleteMsg(m.id); setMenuMsg(null); setMenuAnchor(null); }}>Удалить</MenuItem>}
                      <MenuItem onClick={() => { setPinnedMsgId(m.id); setMenuMsg(null); setMenuAnchor(null); }}>Закрепить</MenuItem>
                    </Menu>
                  </React.Fragment>
                );
              }); })()}
            <div ref={messagesEndRef} />
          </Box>
          {/* Alert ошибок */}
          {msgError && (
            <Box sx={{ position: 'fixed', bottom: 80, left: 0, width: '100vw', display: 'flex', justifyContent: 'center', zIndex: 4000 }}>
              <Alert severity="error" onClose={() => setMsgError('')}>{msgError}</Alert>
            </Box>
          )}
          {/* Ввод сообщения — НЕ absolute/fixed, а просто внизу flex-контейнера */}
          {selected && (
            <Box component="form" onSubmit={send} sx={{
              p: 2,
              display: 'flex',
              gap: 2,
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              zIndex: 110,
              alignItems: 'center',
              position: 'relative',
            }}>
              {/* Скрепка слева */}
              <IconButton onClick={() => fileInputRef.current && fileInputRef.current.click()} sx={{ mr: 1 }}>
                <AttachFileIcon sx={{ color: ACCENT }} />
              </IconButton>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              {/* Поле ввода */}
              <TextField
                value={msg}
                onChange={e => setMsg(e.target.value)}
                placeholder="Введите сообщение..."
                fullWidth
                size="small"
                sx={{ borderRadius: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Гифки */}
                      <IconButton onClick={() => setShowGifPicker(v => !v)} sx={{ mr: 0.5 }}><span role="img" aria-label="gif">🎬</span></IconButton>
                      {/* Эмодзи */}
                      <IconButton onClick={() => setShowEmoji(v => !v)} sx={{ mr: 0.5 }}><span role="img" aria-label="emoji">😊</span></IconButton>
                      <IconButton type="submit" color="primary" disabled={!msg.trim() || uploading}>
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {/* Эмодзи */}
              {showEmoji && (
                <Box sx={{ position: 'absolute', bottom: 56, right: 0, zIndex: 2000 }}>
                  <Picker onEmojiSelect={addEmoji} theme={mode} title="Выберите эмодзи" />
                </Box>
              )}
              {/* Гифки */}
              {showGifPicker && (
                <Box sx={{ position: 'absolute', bottom: 56, right: 60, zIndex: 2000, width: 320, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 4, p: 2 }}>
                  <TextField size="small" fullWidth placeholder="Поиск гифок..." value={gifQuery} onChange={e => setGifQuery(e.target.value)} sx={{ mb: 1 }} />
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {gifResults.map(gif => (
                      <img key={gif.id} src={gif.images.fixed_height_small.url} alt="gif" style={{ width: 80, height: 80, borderRadius: 8, cursor: 'pointer' }} onClick={() => {
                        setMsg(msg + ' ' + gif.images.fixed_height_small.url);
                        setShowGifPicker(false);
                      }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      {/* Модальное окно профиля (заготовка) */}
      {showProfile && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowProfile(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 8 }} onClick={e => e.stopPropagation()}>
            {/* Заголовок и переключатель темы в одной строке */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 3 }}>
              <Typography variant="h6" sx={{ textAlign: 'center' }}>Профиль</Typography>
              <ThemeToggle mode={mode} toggle={toggleTheme} />
            </Box>
            {/* Аватар */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 32, color: ACCENT, cursor: 'pointer', position: 'relative' }} title="Изменить фото" onClick={() => setShowAvatarModal(true)}>
                {user.avatar
                  ? <img src={getAvatarUrl(user.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  : (user.display_name ? user.display_name[0].toUpperCase() : user.username[0].toUpperCase())}
                <Box sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: '#fff', borderRadius: '50%', p: 0.5 }}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path fill={ACCENT} d="M12 5.9c-3.37 0-6.1 2.73-6.1 6.1s2.73 6.1 6.1 6.1 6.1-2.73 6.1-6.1-2.73-6.1-6.1-6.1Zm0 11c-2.7 0-4.9-2.2-4.9-4.9s2.2-4.9 4.9-4.9 4.9 2.2 4.9 4.9-2.2 4.9-4.9 4.9Z"/></svg>
                </Box>
              </Box>
            </Box>
            {/* Имя (редактируемое) */}
            <TextField label="Имя" fullWidth sx={{ mb: 2, maxWidth: 320 }} value={profileName}
              onChange={e => { setProfileName(e.target.value); setProfileNameChanged(e.target.value !== user.display_name && e.target.value.trim().length > 0); }}
            />
            {profileNameChanged && <Button variant="contained" sx={{ mb: 2, maxWidth: 320, width: '100%' }} onClick={async () => {
              const res = await fetch(`${API_URL}/me`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ display_name: profileName })
              });
              if (res.ok) {
                setUser({ ...user, display_name: profileName });
                setProfileMsg('Имя изменено!');
                setProfileNameChanged(false);
              } else {
                setProfileMsg('Ошибка при изменении имени');
              }
            }}>Сохранить</Button>}
            {profileMsg && <Alert severity="success" sx={{ mb: 2 }}>{profileMsg}</Alert>}
            {/* Логин (readonly) */}
            <TextField label="Логин" fullWidth sx={{ mb: 2, maxWidth: 320 }} value={user.username} InputProps={{ readOnly: true }} />
            {/* Смена пароля */}
            <Button variant="outlined" sx={{ mb: 2, maxWidth: 320, width: '100%', color: ACCENT, borderColor: ACCENT }} onClick={() => setShowPwModal(true)}>Сменить пароль</Button>
            {user.is_admin && (
              <Button variant="contained" color="warning" sx={{ mb: 2, maxWidth: 320, width: '100%', color: '#fff', background: ACCENT, '&:hover': { background: ACCENT } }} onClick={async () => {
                setShowAdminPanel(true);
                setAdminLoading(true);
                setAdminError('');
                try {
                  const res = await fetch(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
                  const data = await res.json();
                  setAdminUsers(data);
                } catch {
                  setAdminError('Ошибка загрузки пользователей');
                }
                setAdminLoading(false);
              }}>Администрирование</Button>
            )}
            <Button fullWidth variant="outlined" color="error" sx={{ mt: 3 }} onClick={logout}>Выйти</Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => setShowProfile(false)}>Закрыть</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно поиска пользователей */}
      {showUserSearch && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowUserSearch(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3 }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Поиск пользователей</Typography>
            <TextField
              autoFocus
              fullWidth
              placeholder="Введите имя или логин..."
              value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              sx={{ mb: 2 }}
            />
            {userSearchLoading ? <CircularProgress size={28} /> : (
              <List>
                {userResults.map(u => (
                  <ListItem button key={u.id} onClick={() => createPrivateChat(u)}>
                    <ListItemText primary={u.display_name || u.username} secondary={u.username} />
                  </ListItem>
                ))}
                {userQuery && userResults.length === 0 && !userSearchLoading && (
                  <ListItem><ListItemText primary="Ничего не найдено" /></ListItem>
                )}
              </List>
            )}
            <Button onClick={() => setShowUserSearch(false)} sx={{ mt: 2 }}>Закрыть</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно редактирования сообщения */}
      {editMsg && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEditMsg(null)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3 }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Редактировать сообщение</Typography>
            <TextField fullWidth value={editValue} onChange={e => setEditValue(e.target.value)} multiline minRows={2} sx={{ mb: 2 }} />
            <Button variant="contained" onClick={saveEdit} sx={{ mr: 2 }}>Сохранить</Button>
            <Button variant="outlined" onClick={() => setEditMsg(null)}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно подтверждения удаления */}
      {deleteMsg && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDeleteMsg(null)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3 }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Удалить сообщение?</Typography>
            <Button variant="contained" color="error" onClick={confirmDelete} sx={{ mr: 2 }}>Удалить</Button>
            <Button variant="outlined" onClick={() => setDeleteMsg(null)}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно смены пароля */}
      {showPwModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowPwModal(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3 }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Сменить пароль</Typography>
            <TextField label="Старый пароль" type="password" fullWidth sx={{ mb: 2 }} value={pwFields.old} onChange={e => setPwFields(f => ({ ...f, old: e.target.value }))} />
            <TextField label="Новый пароль" type="password" fullWidth sx={{ mb: 2 }} value={pwFields.new1} onChange={e => setPwFields(f => ({ ...f, new1: e.target.value }))} />
            <TextField label="Повторите новый пароль" type="password" fullWidth sx={{ mb: 2 }} value={pwFields.new2} onChange={e => setPwFields(f => ({ ...f, new2: e.target.value }))} />
            {pwMsg && <Alert severity={pwMsg === 'Пароль изменён!' ? 'success' : 'error'} sx={{ mb: 2 }}>{pwMsg}</Alert>}
            <Button variant="contained" sx={{ mr: 2 }} disabled={!pwFields.old || !pwFields.new1 || !pwFields.new2 || pwFields.new1 !== pwFields.new2} onClick={async () => {
              if (pwFields.new1 !== pwFields.new2) { setPwMsg('Пароли не совпадают'); return; }
              const res = await fetch(`${API_URL}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ old_password: pwFields.old, new_password: pwFields.new1 })
              });
              const data = await res.json();
              if (!res.ok) setPwMsg(data.error || 'Ошибка');
              else setPwMsg('Пароль изменён!');
              setPwFields({ old: '', new1: '', new2: '' });
            }}>Сохранить</Button>
            <Button variant="outlined" onClick={() => setShowPwModal(false)}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно выбора аватара */}
      {showAvatarModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAvatarModal(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Изменить фото</Typography>
            <Button component="label" variant="outlined" sx={{ mb: 2, width: '100%' }}>
              Загрузить с устройства
              <input type="file" accept="image/*" hidden onChange={e => {
                const file = e.target.files[0];
                if (file) {
                  setAvatarFile(file);
                  setAvatarPreview(URL.createObjectURL(file));
                }
              }} />
            </Button>
            <Button variant="outlined" sx={{ mb: 2, width: '100%' }} onClick={async () => {
              // Попытка открыть камеру
              if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                const video = document.createElement('video');
                video.srcObject = stream;
                video.play();
                const canvas = document.createElement('canvas');
                video.onloadedmetadata = () => {
                  canvas.width = video.videoWidth;
                  canvas.height = video.videoHeight;
                  setTimeout(() => {
                    canvas.getContext('2d').drawImage(video, 0, 0);
                    stream.getTracks().forEach(track => track.stop());
                    setAvatarPreview(canvas.toDataURL('image/png'));
                    setAvatarFile(null); // base64
                  }, 800);
                };
              }
            }}>Сделать фото с камеры</Button>
            {avatarPreview && <img src={avatarPreview} alt="preview" style={{ width: 96, height: 96, borderRadius: '50%', marginBottom: 16 }} />}
            <Button variant="contained" sx={{ mb: 2, width: '100%' }} disabled={!avatarPreview} onClick={async () => {
              // Отправка файла или base64 на сервер
              let formData = new FormData();
              if (avatarFile) {
                formData.append('avatar', avatarFile);
              } else if (avatarPreview) {
                formData.append('avatar_base64', avatarPreview);
              }
              const res = await fetch(`${API_URL}/me/avatar`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
              });
              const data = await res.json();
              if (res.ok && data.avatar) {
                setUser({ ...user, avatar: data.avatar });
                setShowAvatarModal(false);
              }
            }}>Сохранить</Button>
            <Button variant="outlined" onClick={() => setShowAvatarModal(false)} sx={{ width: '100%' }}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно админ-панели */}
      {showAdminPanel && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAdminPanel(false)}>
          <Paper sx={{ minWidth: { xs: '98vw', sm: 520 }, maxWidth: 800, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Пользователи</Typography>
            {adminLoading ? <CircularProgress /> : (
              <List sx={{ width: '100%', maxHeight: 600, overflowY: 'auto' }}>
                {adminUsers.map(u => (
                  <ListItem key={u.id} alignItems="center" secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!u.is_approved && <Button size="small" color="success" variant="contained" onClick={async () => {
                        await fetch(`${API_URL}/users/${u.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ is_approved: true })
                        });
                        setAdminUsers(users => users.map(x => x.id === u.id ? { ...x, is_approved: true } : x));
                      }}>Одобрить</Button>}
                      <Button size="small" color="info" variant="outlined" onClick={() => {
                        setEditUser(u);
                        setEditUserFields({
                          display_name: u.display_name || '',
                          username: u.username,
                          is_admin: u.is_admin,
                          is_approved: u.is_approved
                        });
                      }}>Изменить</Button>
                      <Button size="small" color="error" variant="outlined" onClick={async () => {
                        await fetch(`${API_URL}/users/${u.id}`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setAdminUsers(users => users.filter(x => x.id !== u.id));
                      }}>Удалить</Button>
                    </Box>
                  }>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: ACCENT, mr: 2 }}>
                      {u.avatar
                        ? <img src={getAvatarUrl(u.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                        : (u.display_name ? u.display_name[0].toUpperCase() : u.username[0].toUpperCase())}
                    </Box>
                    <ListItemText
                      primary={<span>{u.display_name || u.username} {u.is_admin && <b style={{ color: '#f90' }}>(admin)</b>}</span>}
                      secondary={<span>{u.username} {u.is_approved ? '✔️' : '⏳'}</span>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            {adminError && <Alert severity="error" sx={{ mt: 2 }}>{adminError}</Alert>}
            <Button variant="outlined" onClick={() => setShowAdminPanel(false)} sx={{ mt: 2 }}>Закрыть</Button>
          </Paper>
          {/* Модальное окно редактирования пользователя */}
          {editUser && (
            <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 4000 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Изменить пользователя</Typography>
              <TextField label="Имя" fullWidth sx={{ mb: 2 }} value={editUserFields.display_name} onChange={e => setEditUserFields(f => ({ ...f, display_name: e.target.value }))} />
              <TextField label="Логин" fullWidth sx={{ mb: 2 }} value={editUserFields.username} onChange={e => setEditUserFields(f => ({ ...f, username: e.target.value }))} />
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant={editUserFields.is_admin ? 'contained' : 'outlined'} color="warning" onClick={() => setEditUserFields(f => ({ ...f, is_admin: !f.is_admin }))}>{editUserFields.is_admin ? 'Сделать обычным' : 'Сделать админом'}</Button>
                <Button variant={editUserFields.is_approved ? 'contained' : 'outlined'} color="success" onClick={() => setEditUserFields(f => ({ ...f, is_approved: !f.is_approved }))}>{editUserFields.is_approved ? 'Отклонить' : 'Одобрить'}</Button>
              </Box>
              <Button variant="contained" sx={{ mr: 2 }} onClick={async () => {
                await fetch(`${API_URL}/users/${editUser.id}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify(editUserFields)
                });
                setAdminUsers(users => users.map(x => x.id === editUser.id ? { ...x, ...editUserFields } : x));
                setEditUser(null);
              }}>Сохранить</Button>
              <Button variant="outlined" onClick={() => setEditUser(null)}>Отмена</Button>
            </Paper>
          )}
        </Box>
      )}
      {/* Модальное окно создания группы */}
      {showGroupModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowGroupModal(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 360 }, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Создать группу</Typography>
            <TextField label="Название группы" fullWidth sx={{ mb: 2 }} value={groupTitle} onChange={e => setGroupTitle(e.target.value)} />
            <TextField
              label="Добавить участников"
              fullWidth
              sx={{ mb: 2 }}
              value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              placeholder="Поиск пользователей..."
            />
            <List sx={{ width: '100%', maxHeight: 180, overflowY: 'auto', mb: 2 }}>
              {searchResults.filter(u => !groupMembers.some(m => m.id === u.id)).map(u => (
                <ListItem button key={u.id} onClick={() => setGroupMembers(m => [...m, u])}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: ACCENT, mr: 2 }}>
                    {u.avatar
                      ? <img src={getAvatarUrl(u.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                      : (u.display_name ? u.display_name[0].toUpperCase() : u.username[0].toUpperCase())}
                  </Box>
                  <ListItemText primary={u.display_name || u.username} secondary={u.username} />
                </ListItem>
              ))}
            </List>
            <Box sx={{ width: '100%', mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {groupMembers.map(u => (
                <Button key={u.id} size="small" variant="outlined" onClick={() => setGroupMembers(m => m.filter(x => x.id !== u.id))}>
                  {u.display_name || u.username} ×
                </Button>
              ))}
            </Box>
            <Button variant="contained" fullWidth disabled={!groupTitle.trim()} onClick={async () => {
              const res = await fetch(`${API_URL}/chats`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ title: groupTitle, is_group: true, members: groupMembers.map(u => u.id) })
              });
              const data = await res.json();
              fetch(`${API_URL}/chats`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.json())
                .then(chats => {
                  setChats(chats);
                  setSelected(data.chat_id);
                });
              setShowGroupModal(false);
              setGroupTitle('');
              setGroupMembers([]);
              setUserQuery('');
              setSearchResults([]);
            }}>Создать</Button>
            <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => setShowGroupModal(false)}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модальное окно инфо о группе */}
      {showGroupInfo && groupInfo && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowGroupInfo(false)}>
          <Paper sx={{ minWidth: { xs: '95vw', sm: 420 }, maxWidth: 600, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>Информация о группе</Typography>
            {/* Аватар и название */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 28, color: ACCENT }}>
                {groupInfo.avatar
                  ? <img src={getAvatarUrl(groupInfo.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  : (groupInfo.title ? groupInfo.title[0].toUpperCase() : 'G')}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{groupInfo.title}</Typography>
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{groupMembers.length} участников</Typography>
              </Box>
            </Box>
            {/* Кнопки изменить название/аватар (creator/admin) */}
            {groupMembers.find(u => u.user_id === user.id && ['creator','admin'].includes(u.role)) && (
              <Box sx={{ width: '100%', display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="outlined" sx={{ color: ACCENT, borderColor: ACCENT }} onClick={() => { setEditGroupTitle(groupInfo.title); setShowEditGroupModal('title'); }}>Изменить название</Button>
                <Button variant="outlined" sx={{ color: ACCENT, borderColor: ACCENT }} onClick={() => setShowEditGroupModal('avatar')}>Изменить аватар</Button>
              </Box>
            )}
            {/* Список участников */}
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Участники:</Typography>
            <List sx={{ width: '100%', maxHeight: 220, overflowY: 'auto', mb: 2 }}>
              {groupMembersLoading ? <CircularProgress size={28} /> : groupMembers.map(u => (
                <ListItem key={u.user_id} secondaryAction={
                  groupMembers.find(me => me.user_id === user.id && ['creator','admin'].includes(me.role)) && u.role !== 'creator' ? (
                    <IconButton edge="end" onClick={e => { setMemberMenuAnchor(e.currentTarget); setMemberMenuUser(u); }}>
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#eee"/><text x="12" y="16" textAnchor="middle" fontSize="14" fill="#888">⋮</text></svg>
                    </IconButton>
                  ) : null
                }>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: ACCENT, mr: 2 }}>
                    {u.avatar
                      ? <img src={getAvatarUrl(u.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                      : (u.display_name ? u.display_name[0].toUpperCase() : u.username[0].toUpperCase())}
                  </Box>
                  <ListItemText primary={<span>{u.display_name || u.username} {u.role === 'creator' && <b style={{ color: '#f90' }}>создатель</b>} {u.role === 'admin' && <b style={{ color: ACCENT }}>админ</b>}</span>} secondary={u.username} />
                </ListItem>
              ))}
            </List>
            {/* Меню участника (⋮) */}
            <Menu anchorEl={memberMenuAnchor} open={!!memberMenuAnchor} onClose={() => setMemberMenuAnchor(null)}>
              {memberMenuUser && memberMenuUser.role !== 'admin' && (
                <MenuItem onClick={async () => {
                  await fetch(`${API_URL}/chats/${groupInfo.id}/set-admin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ user_id: memberMenuUser.user_id, make_admin: true })
                  });
                  setMemberMenuAnchor(null);
                  // Обновить участников
                  fetch(`${API_URL}/chats/${groupInfo.id}/members`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json()).then(setGroupMembers);
                }}>Сделать админом</MenuItem>
              )}
              {memberMenuUser && memberMenuUser.role === 'admin' && (
                <MenuItem onClick={async () => {
                  await fetch(`${API_URL}/chats/${groupInfo.id}/set-admin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ user_id: memberMenuUser.user_id, make_admin: false })
                  });
                  setMemberMenuAnchor(null);
                  fetch(`${API_URL}/chats/${groupInfo.id}/members`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json()).then(setGroupMembers);
                }}>Сделать обычным</MenuItem>
              )}
              <MenuItem onClick={async () => {
                await fetch(`${API_URL}/chats/${groupInfo.id}/remove-member/${memberMenuUser.user_id}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${token}` }
                });
                setMemberMenuAnchor(null);
                fetch(`${API_URL}/chats/${groupInfo.id}/members`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.json()).then(setGroupMembers);
              }}>Удалить из группы</MenuItem>
            </Menu>
            {/* Добавить участника (creator/admin) */}
            {groupMembers.find(u => u.user_id === user.id && ['creator','admin'].includes(u.role)) && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <TextField size="small" fullWidth placeholder="Добавить участника..." value={addUserQuery} onChange={e => setAddUserQuery(e.target.value)} sx={{ mb: 1 }} />
                <List sx={{ maxHeight: 120, overflowY: 'auto' }}>
                  {addUserResults.filter(u => !groupMembers.some(m => m.user_id === u.id)).map(u => (
                    <ListItem button key={u.id} onClick={async () => {
                      await fetch(`${API_URL}/chats/${groupInfo.id}/add-member`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ user_id: u.id })
                      });
                      setAddUserQuery('');
                      setAddUserResults([]);
                      fetch(`${API_URL}/chats/${groupInfo.id}/members`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.json()).then(setGroupMembers);
                    }}>
                      <ListItemText primary={u.display_name || u.username} secondary={u.username} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {/* Кнопка удалить/выйти */}
            {groupMembers.find(u => u.user_id === user.id && u.role === 'creator') && (
              <Button variant="contained" color="error" onClick={() => handleDeleteChat(groupInfo.id)} sx={{ width: '100%', mb: 1 }}>Удалить группу</Button>
            )}
            {groupMembers.find(u => u.user_id === user.id && u.role !== 'creator') && (
              <Button variant="contained" color="error" onClick={() => handleDeleteChat(groupInfo.id)} sx={{ width: '100%', mb: 1 }}>Выйти из группы</Button>
            )}
            <Button variant="outlined" onClick={() => setShowGroupInfo(false)} sx={{ width: '100%', mt: 1 }}>Закрыть</Button>
          </Paper>
        </Box>
      )}
      {showContactInfo && contactInfo && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowContactInfo(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 8 }} onClick={e => e.stopPropagation()}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#e0f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 36, color: ACCENT, mb: 2 }}>
              {contactInfo.avatar
                ? <img src={getAvatarUrl(contactInfo.avatar)} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                : (contactInfo.display_name ? contactInfo.display_name[0].toUpperCase() : contactInfo.username[0].toUpperCase())}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22, mb: 1 }}>{contactInfo.display_name || contactInfo.username}</Typography>
            <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 2 }}>{contactInfo.username}</Typography>
            <Button variant="outlined" sx={{ width: '100%', mb: 1 }} onClick={() => setShowContactInfo(false)}>Закрыть</Button>
          </Paper>
        </Box>
      )}
      {showEditGroupModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowEditGroupModal(false)}>
          <Paper sx={{ minWidth: { xs: '90vw', sm: 340 }, p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>{showEditGroupModal === 'title' ? 'Изменить название группы' : 'Изменить аватар группы'}</Typography>
            {showEditGroupModal === 'title' && (
              <>
                <TextField label="Название группы" fullWidth sx={{ mb: 2 }} value={editGroupTitle} onChange={e => setEditGroupTitle(e.target.value)} />
                <Button variant="contained" sx={{ mb: 2, width: '100%' }} disabled={!editGroupTitle.trim()} onClick={async () => {
                  const res = await fetch(`${API_URL}/chats/${groupInfo.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ title: editGroupTitle })
                  });
                  if (res.ok) {
                    setGroupInfo({ ...groupInfo, title: editGroupTitle });
                    setShowEditGroupModal(false);
                  }
                }}>Сохранить</Button>
              </>
            )}
            {showEditGroupModal === 'avatar' && (
              <>
                <Button component="label" variant="outlined" sx={{ mb: 2, width: '100%' }}>
                  Загрузить с устройства
                  <input type="file" accept="image/*" hidden onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setEditGroupAvatarFile(file);
                      setEditGroupAvatarPreview(URL.createObjectURL(file));
                    }
                  }} />
                </Button>
                {editGroupAvatarPreview && <img src={editGroupAvatarPreview} alt="preview" style={{ width: 96, height: 96, borderRadius: '50%', marginBottom: 16 }} />}
                <Button variant="contained" sx={{ mb: 2, width: '100%' }} disabled={!editGroupAvatarPreview} onClick={async () => {
                  let formData = new FormData();
                  if (editGroupAvatarFile) {
                    formData.append('avatar', editGroupAvatarFile);
                  }
                  const res = await fetch(`${API_URL}/chats/${groupInfo.id}`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setGroupInfo({ ...groupInfo, avatar: data.avatar || groupInfo.avatar });
                    setChats(chats => chats.map(c => c.id === groupInfo.id ? { ...c, avatar: data.avatar || c.avatar } : c));
                    setShowEditGroupModal(false);
                  }
                }}>Сохранить</Button>
              </>
            )}
            <Button variant="outlined" onClick={() => setShowEditGroupModal(false)} sx={{ width: '100%' }}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Модалка пересылки */}
      {showForwardModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowForwardModal(false)}>
          <Paper sx={{ p: 3, minWidth: 320 }} onClick={e => e.stopPropagation()}>
            <Typography sx={{ mb: 2, fontWeight: 700 }}>Выберите чат для пересылки</Typography>
            <List>
              {chats.filter(c => c.id !== selected).map(c => (
                <ListItem button key={c.id} onClick={() => { setShowForwardModal(false); alert('Сообщение переслано в чат: ' + (c.title || c.members?.map(u => u.display_name || u.username).join(', '))); }}>
                  <ListItemText primary={c.title || (c.members?.map(u => u.display_name || u.username).join(', '))} />
                </ListItem>
              ))}
            </List>
            <Button onClick={() => setShowForwardModal(false)} sx={{ mt: 2 }}>Отмена</Button>
          </Paper>
        </Box>
      )}
      {/* Lightbox для фото/видео */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" onKeyDown={e => { if (e.key === 'Escape') setPreview(null); }}>
        <div style={{ background: '#111', padding: 16, textAlign: 'center' }} onClick={() => setPreview(null)}>
          {preview?.type?.startsWith('image/') && (
            <img src={preview.url || '/default.png'} alt={preview.name} style={{ maxWidth: '90vw', maxHeight: '80vh' }} onClick={e => e.stopPropagation()} />
          )}
          {preview?.type?.startsWith('video/') && (
            <video src={preview.url || '#'} controls style={{ maxWidth: '90vw', maxHeight: '80vh' }} onClick={e => e.stopPropagation()} />
          )}
          <div style={{ marginTop: 12 }}>
            <a href={preview?.url || '#'} download={preview?.name} style={{ color: '#00AEEF', fontWeight: 500, fontSize: 16 }} target="_blank" rel="noopener noreferrer">
              Скачать
            </a>
          </div>
        </div>
      </Dialog>
    </Box>
  );
}

function formatLastActive(lastActive) {
  const date = new Date(lastActive);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff/60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff/3600)} ч назад`;
  return date.toLocaleDateString();
}

function App() {
  const [mode, setMode] = useState(() => localStorage.getItem('theme') || 'light');
  const theme = useMemo(() => createTheme({
    palette: { mode },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: MODERN_FONT,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: MODERN_FONT,
            letterSpacing: 0.01,
            fontWeight: 400,
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { position: 'relative', fontFamily: MODERN_FONT } } },
      MuiButton: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiTypography: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiList: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiListItemText: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiTextField: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiInputBase: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
      MuiMenuItem: { styleOverrides: { root: { fontFamily: MODERN_FONT } } },
    }
  }), [mode]);
  const toggle = () => {
    setMode(m => {
      const next = m === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', next);
      return next;
    });
  };
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [user, setUser] = useState(null);

  // Проверка токена и получение профиля
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { if (data.user) setUser(data.user); else { setToken(''); setUser(null); localStorage.removeItem('token'); } });
  }, [token]);

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js');
    });
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {!token || !user ? (
        <>
          <Auth setToken={setToken} setUser={setUser} />
          <Footer />
        </>
      ) : (
        <Messenger token={token} user={user} setUser={setUser} logout={() => { setToken(''); setUser(null); localStorage.removeItem('token'); }} mode={mode} toggleTheme={toggle} />
      )}
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
); 